/**
 * CroFunnelPacingTile — CRO-05 Total Pipeline Funnel as a cumulative trend chart.
 *
 * Phase 04.1 #2 (2026-07-10). Replaces the CRO-05 big-number tile with a
 * cumulative-by-close-month area line: x = close month, y = cumulative open
 * Product pipeline $. Funnel-volume view — no target, no overlay. Reuses the
 * CroTile/CroTrendTile 4-state machine + the CRO-05 definition for title/tooltip.
 */
import ReactECharts from 'echarts-for-react';

import {
  CacheNotReadyError,
  SalesforceUnavailableError,
} from '../lib/api-client.js';
import { getCroMetricDefinition } from '../lib/definitions.js';
import { formatCurrency } from '../lib/format.js';

import { SfUnreachableBanner } from './SfUnreachableBanner.js';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card.js';
import { Skeleton } from './ui/Skeleton.js';
import { Tooltip } from './ui/Tooltip.js';

import type { CroFunnelPacingResponse } from '@saledash/shared';

interface CroFunnelPacingTileProps {
  data: CroFunnelPacingResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

/** ECharts option (returned as `object` — see CroArrPacingTile for the rationale). */
function buildFunnelOption(data: CroFunnelPacingResponse): object {
  const money = (v: number): string => formatCurrency(v);
  return {
    animation: false,
    grid: { left: 46, right: 16, top: 16, bottom: 24 },
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(17,24,39,0.9)',
      borderColor: 'transparent',
      textStyle: { color: '#fff', fontSize: 11 },
      formatter: (params: { axisValue: string; value: number }[]) => {
        const p = params[0];
        if (!p) return '';
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion -- ECharts tooltip params are untyped at runtime; keep the numeric coercion
        return `${p.axisValue}<br/>Cumulative open: <strong>${money(Number(p.value))}</strong>`;
      },
    },
    xAxis: {
      type: 'category',
      data: data.labels,
      boundaryGap: false,
      axisLabel: { fontSize: 9, color: '#6b7280' },
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      axisLabel: { fontSize: 9, color: '#6b7280', formatter: (v: number) => money(v) },
      splitLine: { lineStyle: { color: '#f3f4f6' } },
    },
    series: [
      {
        name: 'Cumulative open pipeline',
        type: 'line',
        data: data.cumulative,
        smooth: false,
        symbol: 'circle',
        symbolSize: 5,
        lineStyle: { color: '#2851e6', width: 2.5 },
        itemStyle: { color: '#2851e6' },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(40,81,230,0.18)' },
              { offset: 1, color: 'rgba(40,81,230,0)' },
            ],
          },
        },
      },
    ],
  };
}

export function CroFunnelPacingTile({
  data,
  isLoading,
  isError,
  error,
  refetch,
}: CroFunnelPacingTileProps): React.JSX.Element {
  const definition = getCroMetricDefinition('CRO-05');
  const tooltipContent = (
    <>
      <p className="mb-1 font-semibold text-ink-900">{definition.name}</p>
      <p>{definition.formula_plain_english}</p>
      <p className="mt-2 text-[10px] uppercase tracking-wide text-ink-500">
        Source: {definition.source_of_truth_system}
      </p>
    </>
  );

  const header = (
    <CardHeader>
      <CardTitle className="text-[0.72rem] uppercase tracking-wider font-semibold text-ink-600">
        {definition.name}
      </CardTitle>
      <Tooltip trigger="?" label="Show definition">{tooltipContent}</Tooltip>
    </CardHeader>
  );

  if (isError && error instanceof CacheNotReadyError) {
    return (
      <Card data-testid="cro-tile-CRO-05">
        {header}
        <CardContent>
          <div
            role="status"
            className="flex items-start gap-3 rounded-lg border border-warn bg-warn-bg px-4 py-3 text-sm text-warn-text"
            data-testid="cache-warming-banner"
          >
            <span aria-hidden className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-warn text-xs font-bold text-white">⌛</span>
            <div className="flex-1">
              <p className="font-semibold">Funnel chart warming up</p>
              <p className="mt-1 text-xs text-warn-text/80">First open-pipeline-by-month refresh expected within ~7 minutes.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError && error instanceof SalesforceUnavailableError) {
    return (
      <Card data-testid="cro-tile-CRO-05">
        {header}
        <CardContent><SfUnreachableBanner onRetry={refetch} /></CardContent>
      </Card>
    );
  }

  if (isLoading || !data) {
    return (
      <Card data-testid="cro-tile-CRO-05">
        {header}
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-9 w-40" />
            <Skeleton className="h-[220px] w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="cro-tile-CRO-05">
      {header}
      <CardContent>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <p className="text-3xl font-bold tabular-nums text-ink-900" data-testid="kpi-value">
            {formatCurrency(data.total)}
          </p>
          <span className="text-sm text-ink-600">
            total open pipeline
            <span className="ml-1 text-ink-400">· {String(data.opp_count)} opps</span>
          </span>
        </div>

        <div className="mt-3 h-[220px] w-full" data-testid="funnel-chart">
          <ReactECharts
            option={buildFunnelOption(data)}
            style={{ height: '100%', width: '100%' }}
            opts={{ renderer: 'canvas' }}
            notMerge
            lazyUpdate={false}
          />
        </div>
      </CardContent>
    </Card>
  );
}
